import { http } from '@services/http/client';
import type {
  CheckoutSession,
  Invoice,
  Paginated,
  Plan,
  PlanInterval,
  PortalSession,
  Subscription,
  SubscriptionStatus,
} from '@app-types/index';

import { mapPaginated, type RawPaginated } from './_shared';
import { ENDPOINTS } from './endpoints';

// ---------------------------------------------------------------------------
// Wire-format types (snake_case, as the backend serializers emit them).
// ---------------------------------------------------------------------------
interface RawPlan {
  id: string;
  name: string;
  max_aliases: number | null;
  monthly_forward_limit: number | null;
  amount_cents: number;
  currency: string;
  interval: PlanInterval;
  feature_list: string[];
}

interface RawSubscription {
  id: string;
  plan: RawPlan;
  status: SubscriptionStatus;
  is_entitled: boolean;
  current_period_end: string | null;
  grace_period_until: string | null;
  cancel_at_period_end: boolean;
}

interface RawSubscriptionMe {
  subscription: RawSubscription | null;
}

interface RawInvoice {
  id: string;
  date: string;
  amount_cents: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
}

interface RawCheckoutSession {
  id: string;
  url: string;
}

interface RawPortalSession {
  url: string;
}

const mapPlan = (raw: RawPlan): Plan => ({
  id: raw.id,
  name: raw.name,
  maxAliases: raw.max_aliases,
  monthlyForwardLimit: raw.monthly_forward_limit,
  amountCents: raw.amount_cents,
  currency: raw.currency,
  interval: raw.interval,
  featureList: raw.feature_list,
});

const mapSubscription = (raw: RawSubscription): Subscription => ({
  id: raw.id,
  plan: mapPlan(raw.plan),
  status: raw.status,
  isEntitled: raw.is_entitled,
  currentPeriodEnd: raw.current_period_end,
  gracePeriodUntil: raw.grace_period_until,
  cancelAtPeriodEnd: raw.cancel_at_period_end,
});

// The cancel/change/confirm/resume actions all return the same
// {subscription: <obj|null>} envelope as `me`.
const mapSubscriptionMe = (raw: RawSubscriptionMe): Subscription | null =>
  raw.subscription ? mapSubscription(raw.subscription) : null;

const mapInvoice = (raw: RawInvoice): Invoice => ({
  id: raw.id,
  date: raw.date,
  amountCents: raw.amount_cents,
  currency: raw.currency,
  status: raw.status,
  hostedInvoiceUrl: raw.hosted_invoice_url,
});

// ---------------------------------------------------------------------------
// Request body shapes (camelCase in — snake_case on the wire).
// ---------------------------------------------------------------------------
export interface CheckoutInput {
  planId: string;
  successUrl: string;
  cancelUrl: string;
}

export interface PortalInput {
  returnUrl: string;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const billingApi = {
  plans: async (): Promise<Paginated<Plan>> => {
    const raw = await http.get<unknown, RawPaginated<RawPlan>>(ENDPOINTS.billing.plans);
    return mapPaginated(raw, mapPlan);
  },

  // null when the user has never subscribed — callers render an empty state.
  mySubscription: async (): Promise<Subscription | null> => {
    const raw = await http.get<unknown, RawSubscriptionMe>(ENDPOINTS.billing.subscriptionMe);
    return mapSubscriptionMe(raw);
  },

  // Returns the Stripe Checkout session; caller redirects to `url`.
  checkoutSession: async (input: CheckoutInput): Promise<CheckoutSession> => {
    const data = await http.post<unknown, RawCheckoutSession>(
      ENDPOINTS.billing.checkoutSession,
      {
        plan_id: input.planId,
        success_url: input.successUrl,
        cancel_url: input.cancelUrl,
      },
    );
    return { id: data.id, url: data.url };
  },

  // Reconcile right after the Checkout redirect (beats the webhook race).
  confirmCheckout: async (sessionId: string): Promise<Subscription | null> => {
    const raw = await http.post<unknown, RawSubscriptionMe>(
      ENDPOINTS.billing.confirmCheckout,
      { session_id: sessionId },
    );
    return mapSubscriptionMe(raw);
  },

  // Upgrade/downgrade an existing subscription in place (no new checkout).
  changePlan: async (planId: string): Promise<Subscription | null> => {
    const raw = await http.post<unknown, RawSubscriptionMe>(ENDPOINTS.billing.changePlan, {
      plan_id: planId,
    });
    return mapSubscriptionMe(raw);
  },

  // Schedule cancellation at the end of the current billing period.
  cancel: async (): Promise<Subscription | null> => {
    const raw = await http.post<unknown, RawSubscriptionMe>(ENDPOINTS.billing.cancel, {});
    return mapSubscriptionMe(raw);
  },

  // Undo a scheduled cancellation.
  resume: async (): Promise<Subscription | null> => {
    const raw = await http.post<unknown, RawSubscriptionMe>(ENDPOINTS.billing.resume, {});
    return mapSubscriptionMe(raw);
  },

  // Returns the Stripe Billing Portal session; caller redirects to `url`.
  portalSession: async (input: PortalInput): Promise<PortalSession> => {
    const data = await http.post<unknown, RawPortalSession>(
      ENDPOINTS.billing.portalSession,
      { return_url: input.returnUrl },
    );
    return { url: data.url };
  },

  // Non-standard envelope: {results:[...]} with no pagination metadata.
  invoices: async (): Promise<Invoice[]> => {
    const raw = await http.get<unknown, { results: RawInvoice[] }>(ENDPOINTS.billing.invoices);
    return (raw.results ?? []).map(mapInvoice);
  },
};
